import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/api/client";

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "Hi, this is CityShor customer support. How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMsgs((prev) => [...prev, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMsgs((prev) => [...prev, { role: "bot", text: data.reply }]);
    } catch {
      setMsgs((prev) => [...prev, { role: "bot", text: "Sorry, I couldn't respond right now." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="ai-chat-toggle" onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105">
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </button>

      {open && (
        <div data-testid="ai-chat-panel" className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] max-w-sm h-[520px] bg-white rounded-2xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-[#F48E2E] to-[#111827] text-white">
            <div className="flex items-center gap-2">
              <img src="/cityshor-logo.png" alt="" className="h-6 w-6 rounded-full object-cover" />
              <div className="font-display text-lg">CityShor Assistant</div>
            </div>
            <div className="text-[11px] opacity-90">We usually reply in a few minutes</div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-[#F48E2E] text-white rounded-br-md" : "bg-white border border-gray-200 text-gray-900 rounded-bl-md"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-gray-500">Thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-gray-200 flex gap-2 bg-white">
            <input data-testid="ai-chat-input"
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about today's drops…"
              className="flex-1 px-3 py-2 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F48E2E]/40"/>
            <Button data-testid="ai-chat-send" onClick={send} size="icon" className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
